// User Profile Types - TypeScript interfaces matching C# DTOs
// Based on /api/me and /api/users endpoints

// ====================
// ENUMS
// ====================

export enum ProfileVisibility {
  Private = 0,
  FriendsOnly = 1,
  FriendsAndGroupMembers = 2,
  Public = 3,
}

export enum AgeVisibility {
  Private = 0,
  AgeOnly = 1,
  MonthAge = 2,
  MonthYearAge = 3,
}

export enum UserRank {
  // TODO: Add rank values from backend
  Newbie = 0,
  Explorer = 1,
  Adventurer = 2,
  // Add more as defined in backend
}

// ====================
// INTERFACES
// ====================

export interface SocialMediaLinkDto {
  platform: string
  url: string
  isPublic: boolean // Deprecated, kept for backwards compatibility
  visibility: ProfileVisibility // Per-link privacy setting
}

export interface RankInfo {
  // TODO: Define based on backend schema
  [key: string]: any
}

export interface UserProfileDto {
  id: string
  userId: string | null
  email: string | null
  isEmailVerified: boolean
  phoneNumber: string | null
  isPhoneVerified: boolean
  displayName: string | null
  avatar: string | null
  coverPhoto: string | null
  bio: string | null
  hometown: string | null
  profileVisibility: ProfileVisibility
  showAge: AgeVisibility
  socialLinksVisible: boolean
  ageInMonths: number | null
  genderIdentityTagId: string | null // Tag GUID/ID for gender identity (read from API, same as genderIdentity)
  genderIdentity: string | null // Tag GUID/ID (same as genderIdentityTagId, kept for backwards compatibility)
  genderVisibility: ProfileVisibility // Privacy setting for gender identity
  ageRange: string | null // Read-only, calculated by backend
  socialMediaLinks: Record<string, SocialMediaLinkDto> | null // Dictionary with platform as key
  interestTagIds: string[] | null // Array of tag GUIDs/IDs (read from API, same as interestTags)
  interestTags: string[] | null // Array of tag GUIDs/IDs (same as interestTagIds, kept for backwards compatibility)
  interestsVisibility: ProfileVisibility // Privacy setting for interest tags
  lookingForTagIds: string[] | null // Array of tag GUIDs/IDs (read from API, same as lookingFor)
  lookingFor: string[] | null // Array of tag GUIDs/IDs (same as lookingForTagIds, kept for backwards compatibility)
  lookingForVisibility: ProfileVisibility // Privacy setting for looking for tags
  rank: UserRank
  rankInfo: RankInfo | null
  rankName: string | null
  rankBadge: string | null
  pointsToNextRank: number
  nextRankName: string | null
  lastDisplayNameChange: string | null // ISO date string
  displayNameChangesThisYear: number
  referralCode: string | null // User's unique referral code (only visible to self)
  createdDate: string // ISO date string
  updatedDate: string // ISO date string
}

export interface UpdateUserProfileDto {
  displayName?: string
  avatar?: string | null
  coverPhoto?: string | null
  bio?: string
  hometown?: string
  profileVisibility?: ProfileVisibility
  showAge?: AgeVisibility
  socialLinksVisible?: boolean
  ageInMonths?: number | null
  genderIdentityTagId?: string | null  // Tag GUID/ID
  genderVisibility?: ProfileVisibility
  socialMediaLinks?: Record<string, SocialMediaLinkDto>
  interestTagIds?: string[]  // Array of tag GUIDs/IDs
  interestsVisibility?: ProfileVisibility
  lookingForTagIds?: string[]    // Array of tag GUIDs/IDs
  lookingForVisibility?: ProfileVisibility
  // Additional fields for compatibility
  email?: string
  phoneNumber?: string
  billingAddress?: {
    street?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
  }
}

// ====================
// API RESPONSES
// ====================

export interface ProfileApiResponse<T> {
  success: boolean
  data?: T
  message?: string
  errors?: string[]
}
